<template>
    <template v-if="post">
        <!-- Content Section -->
        <section class="pt-12 pb-16">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="max-w-3xl mx-auto">
                    <div class="mb-8">
                        <router-link
                            :to="`/${currentLocale}/blog`"
                            class="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-accent transition-all hover:-translate-x-0.5 mb-8"
                        >
                            <ArrowLeftIcon class="w-4 h-4" />
                            {{ $t('blog.backToBlog') }}
                        </router-link>

                        <div>
                            <time
                                :datetime="post.publishedAt"
                                class="px-3 py-1 bg-muted text-muted-foreground text-xs font-medium rounded-full tracking-wide uppercase"
                            >
                                {{ formatDate(post.publishedAt) }}
                            </time>
                        </div>

                        <h1 class="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mt-5">
                            {{ post.title }}
                        </h1>
                    </div>

                    <img
                        v-if="post.image"
                        :src="post.image"
                        :alt="post.title"
                        class="w-full rounded-xl mb-10"
                    >

                    <!-- eslint-disable-next-line vue/no-v-html -- Safe: content is developer-authored markdown compiled at build time -->
                    <article class="prose" v-html="post.html" />
                </div>
            </div>
        </section>

        <!-- Dynamic component teleports — mounted into placeholder divs inside v-html content -->
        <template v-for="comp in resolvedComponents" :key="comp.target">
            <Teleport v-if="activeTeleports.includes(comp.target)" :to="`#${comp.target}`">
                <component :is="comp.component" />
            </Teleport>
        </template>
    </template>

    <template v-else>
        <section class="py-20 bg-muted/30">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="text-center py-16 max-w-lg mx-auto">
                    <h1 class="text-2xl font-bold text-foreground mb-3">
                        {{ $t('blog.notFound') }}
                    </h1>
                    <p class="text-muted-foreground mb-6">
                        {{ $t('blog.notFoundDescription') }}
                    </p>
                    <router-link
                        :to="`/${currentLocale}/blog`"
                        class="inline-flex items-center gap-1.5 text-accent font-medium hover:gap-2.5 transition-all"
                    >
                        <ArrowLeftIcon class="w-4 h-4" />
                        {{ $t('blog.backToBlog') }}
                    </router-link>
                </div>
            </div>
        </section>
    </template>
</template>

<script lang="ts">
import { defineComponent, computed, ref, onMounted, nextTick, watch, defineAsyncComponent, type Component } from 'vue';
import { useRoute } from 'vue-router';
import { getBlogPost } from '@/composables/useBlogContent';
import { useBlogPostSeoHead } from '@/composables/useBlogSeoHead';
import { ArrowLeftIcon } from '@heroicons/vue/24/outline';

// Registry of components that can be embedded in blog posts via frontmatter.
// Add new embeddable components here — they are lazy-loaded and only fetched
// when a post actually declares them.
const componentRegistry: Record<string, Component> = {
    CostCalculator: defineAsyncComponent<Component>(() => import('@/components/blog/CostCalculator.vue')),
};

export default defineComponent({
    name: 'BlogPost',
    components: {
        ArrowLeftIcon,
    },
    setup() {
        const route = useRoute();

        const currentLocale = computed(() => {
            const routeLocale = route.params.locale as string;
            return (routeLocale === 'en' || routeLocale === 'lt') ? routeLocale : 'en';
        });

        const slug = computed(() => route.params.slug as string);

        const post = computed(() => getBlogPost(currentLocale.value, slug.value));

        // SEO head — only set when post exists
        const resolvedPost = post.value;
        if (resolvedPost) {
            useBlogPostSeoHead(resolvedPost);
        }

        const formatDate = (dateStr: string) => {
            const date = new Date(dateStr);
            return date.toLocaleDateString(currentLocale.value === 'lt' ? 'lt-LT' : 'en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            });
        };

        // Tracks which placeholder div IDs are currently present in the DOM.
        // Updated after each render so Teleports only activate for existing targets.
        const activeTeleports = ref<string[]>([]);

        const updateActiveTeleports = () => {
            void nextTick(() => {
                if (typeof document === 'undefined') {
                    return;
                }
                const components = post.value?.components ?? [];
                activeTeleports.value = components
                    .filter(c => c.name in componentRegistry)
                    .filter(c => !!document.getElementById(c.target))
                    .map(c => c.target);
            });
        };

        onMounted(updateActiveTeleports);
        watch(post, updateActiveTeleports);

        // Maps active teleport targets to their resolved async components
        const resolvedComponents = computed(() => {
            const components = post.value?.components ?? [];
            return components
                .filter(c => c.name in componentRegistry)
                .map(c => ({ target: c.target, component: componentRegistry[c.name] }));
        });

        return {
            post,
            currentLocale,
            formatDate,
            activeTeleports,
            resolvedComponents,
        };
    },
});
</script>
